import Fuse from 'fuse.js';
import moment from 'moment-timezone';
import React, { Fragment, ReactNode } from 'react';

import { defaultEntityTypeOptions } from 'shared/hooks';
import { humanizeToCorrectTitleCase } from 'shared/utils';

import { deserializeQuery, serializeQuery } from './serializer';
import {
  DateFilter,
  DateFilterModifier,
  DateTimeUnit,
  dateTimeUnits,
  Field,
  FieldTypes,
  isNegatableOptionsList,
  ListField,
  ListFieldOption,
  NegatableListFieldOption,
} from './types';
import {
  DateTimeFilter,
  FilterTypes,
  PropBasedFilterTypes,
  RangeFilter,
  ValueBasedFilterTypes,
} from './types/filterTypes';

const dateFormat = 'L';
const dateTimeFormat = 'L LT';
const dateTimeWithSecondsFormat = 'L LTS';

const NO_NUMERIC_FORMAT_PROP_SUFFIX = ['asnNumber', 'Code', 'Port'];

/**
 * Return the date format for the given conditions.
 */
export function getDateFormat(
  showTime: boolean = false,
  includeSeconds: boolean = false
) {
  return showTime
    ? includeSeconds
      ? dateTimeWithSecondsFormat
      : dateTimeFormat
    : dateFormat;
}

/**
 * Return the date time unit for the given modifier.
 */
function getModifierObject(modifierType: DateTimeUnit) {
  if (modifierType) {
    return dateTimeUnits.find((unit) => unit.type === modifierType);
  }
}

/**
 * Get the operator metadata given a filter and operator type.
 */
export function getOperatorObject(
  operatorType: string,
  filter: PropBasedFilterTypes,
  fields: FieldTypes[]
) {
  if (operatorType) {
    const fieldObject = getFieldObject(filter.prop, fields);
    operatorType = operatorType.toLowerCase();

    if (fieldObject) {
      return fieldObject.operators.find(
        (operator) =>
          operator.label.toLowerCase() === operatorType ||
          operator.type.toLowerCase() === operatorType
      );
    }
  }
}

/**
 * Get the field given a prop.
 */
export function getFieldObject(fieldProp: string, fields: FieldTypes[]) {
  if (fieldProp) {
    fieldProp = fieldProp.toLowerCase();

    return fields.find(
      (field) =>
        field.label !== undefined &&
        (field.label.toLowerCase() === fieldProp ||
          field.prop.toLowerCase() === fieldProp)
    );
  }
}

/**
 * Generically filters a list of items with label prop.
 */
export function filterGenericList(listItems: any[], searchText?: string) {
  if (!searchText) {
    return listItems;
  }

  const fuse = new Fuse(listItems, {
    shouldSort: true,
    threshold: 0.3,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    caseSensitive: false,
    minMatchCharLength: 2,
    includeMatches: true,
    keys: ['label'],
  });

  const results = fuse.search(searchText);
  return results.map((result: any) => {
    const item = {
      ...result.item,
      formattedLabel: undefined,
    };

    const label = item.label;
    let i = 0;
    const html: ReactNode[] = [];

    for (const m of result.matches) {
      const [start, end] = m.indices[0];
      html.push(label.substring(i, start));
      html.push(<mark>{label.substring(start, end + 1)}</mark>);
      i = end + 1;
    }

    html.push(label.substring(i, label.length));
    item.formattedLabel = <Fragment>{html}</Fragment>;

    return item;
  });
}

/**
 * Filter the fields list given search text.
 */
export function getFilteredFieldsList(
  fields: FieldTypes[],
  activeSource: string,
  inputText?: string
) {
  const filteredFields = fields.filter(
    (field) => activeSource === field.source || activeSource === 'all'
  );
  if (inputText) {
    return filterGenericList(filteredFields, inputText);
  } else {
    return filteredFields;
  }
}

/**
 * Filter the operator list given a filter.
 */
export function getFilteredOperatorsList(
  partialFilter: Partial<PropBasedFilterTypes>,
  fields: FieldTypes[],
  inputText?: string
) {
  if (partialFilter.prop) {
    const partialFilterField = getFieldObject(partialFilter.prop, fields);

    if (partialFilterField) {
      if (inputText) {
        return filterGenericList(partialFilterField.operators, inputText);
      } else {
        return partialFilterField.operators;
      }
    }
  }

  return [];
}

/**
 * Filter the operator list given a filter.
 */
export function getFilteredOptionsList(
  partialFilter: Partial<PropBasedFilterTypes>,
  fields: FieldTypes[],
  inputText?: string
) {
  if (partialFilter.prop) {
    const partialFilterField = getFieldObject(partialFilter.prop, fields);

    if (partialFilterField && partialFilterField.type === 'list') {
      const listField = partialFilterField as ListField;
      if (listField.options) {
        if (inputText) {
          return filterGenericList(listField.options, inputText);
        } else {
          return listField.options;
        }
      }
    }
  }

  return [];
}

/**
 * Given a field and filter, get the formatted label.
 */
// complexity 28
// eslint-disable-next-line complexity
export function getFilterValueLabel(field: Field, filter: any) {
  let value = filter.value;

  if (filter.value && field) {
    if (field.type === 'date') {
      const dateFilter = filter as DateFilter;
      const dateTimeFilter = filter as DateTimeFilter;
      const rawValue =
        dateFilter.value instanceof Date
          ? dateFilter.value
          : dateTimeFilter.value.value;
      const rawMomentValue = moment(rawValue);
      const hasTime = !['equals', 'doesNotEqual'].includes(dateFilter.operator);

      if (!hasTime) {
        const parsedDate = moment.utc(rawValue);
        const today = moment(new Date()).startOf('day');
        const yesterday = today.clone().subtract(1, 'day');

        if (today.isSame(parsedDate)) {
          value = 'today';
        } else if (yesterday.isSame(parsedDate)) {
          value = 'yesterday';
        } else {
          value = rawMomentValue.format(dateFormat);
        }
      } else {
        const hasSecond = rawMomentValue.second() > 0;
        const format = getDateFormat(hasTime, hasSecond);
        value = moment(rawValue).format(format);
      }
    } else if (field.type === 'list') {
      const listField = field as ListField;

      if (listField.options) {
        const listOption = listField.options.find(
          (o) => o.prop === filter.value
        );

        if (listOption) {
          value = listOption.label;
        }
      } else {
        value = filter.value;
      }
    } else if (field.type === 'numeric' && !needNumberFormat(field.prop)) {
      value = filter.value.toString();
    } else if (filter.value !== undefined) {
      value = filter.value.toLocaleString();
    }
  } else if (filter.value === null) {
    value = field?.customNullValue || 'Empty';
  } else if (filter.value !== undefined) {
    value = filter.value.toLocaleString();
  } else if (filter.start && filter.end && isRelativeRangeFilter(filter)) {
    let modifier: DateFilterModifier;

    if (filter.start.modifiers.length > 0) {
      modifier = filter.start.modifiers[0];
    } else {
      modifier = filter.end.modifiers[0];
    }

    const unit = getModifierObject(modifier.unit);
    value =
      Math.abs(modifier.value).toLocaleString() +
      ' ' +
      unit!.label.toLocaleLowerCase();
  } else if (filter.start && filter.end && isDateRangeFilter(filter)) {
    const rawVal = filter.start.value;
    const rawEndVal = filter.end.value;

    const rawMoment = moment(rawVal);
    const rawEndMoment = moment(rawEndVal);

    const hasTime =
      rawMoment.hour() > 0 ||
      rawMoment.minute() > 0 ||
      rawEndMoment.hour() > 0 ||
      rawEndMoment.minute() > 0;
    const hasSecond = rawMoment.second() > 0 || rawEndMoment.second() > 0;
    const format = getDateFormat(hasTime, hasSecond);

    value =
      moment(rawVal).format(format) + ' - ' + moment(rawEndVal).format(format);
  }

  return value;
}

/**
 * Filter the date modifiers list.
 */
export function getFilteredModifiersList(inputText?: string) {
  if (inputText) {
    return filterGenericList(dateTimeUnits, inputText);
  } else {
    return dateTimeUnits;
  }
}

/**
 * Flattens the groups to get an accurate index.
 */
export function flattenOptionGroups(
  options: ListFieldOption[] | NegatableListFieldOption[],
  groups?: string[]
) {
  if (!groups) {
    return options;
  }

  if (isNegatableOptionsList(options)) {
    return groups.reduce((accumulator: NegatableListFieldOption[], group) => {
      const groupedOptions = options.filter((o) => o.group === group);
      for (const option of groupedOptions) {
        accumulator.push(option);
      }
      return accumulator;
    }, []);
  } else {
    return groups.reduce((accumulator: ListFieldOption[], group) => {
      const groupedOptions = options.filter((o) => o.group === group);
      for (const option of groupedOptions) {
        accumulator.push(option);
      }
      return accumulator;
    }, []);
  }
}

/**
 * Checks to see if a date filter is a relative range
 */
export function isRelativeRangeFilter(filter: Partial<FilterTypes>) {
  const dateFilter = filter as DateFilter;
  return dateFilter && ['past', 'next'].includes(dateFilter.operator);
}

/**
 * Checks to see if a date filter is a specific range
 */
export function isDateRangeFilter(filter: Partial<FilterTypes>) {
  const dateFilter = filter as RangeFilter;
  return dateFilter && dateFilter.operator === 'between';
}

/**
 * Checks to see if a filter is an equality filter
 */
export function isEqualityFilter(filter: Partial<FilterTypes>) {
  const equalityFilter = filter as ValueBasedFilterTypes;
  return ['equals', 'doesNotEqual'].includes(equalityFilter.operator);
}

/**
 * Checks to see if a filter is a key value filter
 */
export const isKeyValueFilter = (filter: Partial<FilterTypes>) =>
  isKeyValueOperator((filter as ValueBasedFilterTypes).operator);

/**
 * Checks to see if an operator is a tag value operator
 */
export const isKeyValueOperator = (operator: string) =>
  ['containKeyValue', 'doNotContainKeyValue'].includes(operator);

/**
 * Returns the start and end dates of user input in the correct order
 */
export function getRangeDatesFromInput(rangeText: string, format: string) {
  const [firstDate, secondDate] = rangeText.split(' - ', 2);

  // If the split has a `_` in it, its not considered valid
  // to be parsed so pass undefined
  const firstMoment = moment(
    firstDate && firstDate.indexOf('_') === -1 ? firstDate : undefined,
    format
  );
  const secondMoment = moment(
    secondDate && secondDate.indexOf('_') === -1 ? secondDate : undefined,
    format
  );

  // If we are typing it out and we have the first value finished
  // but not the last, lets return only valid first
  const rangeStart =
    firstMoment.isBefore(secondMoment) || !secondMoment.isValid()
      ? firstMoment
      : secondMoment;

  // If a user puts in a second date that before the first,
  // lets reset it to the first value
  const rangeEnd = secondMoment.isValid()
    ? firstMoment.isBefore(secondMoment)
      ? secondMoment
      : firstMoment
    : secondMoment;

  return {
    rangeStart,
    rangeEnd,
  };
}

/**
 *  Returns filter field, operator, and value labels
 */
// complexity is 13
// eslint-disable-next-line complexity
export function getFilterLabels(filter: FilterTypes, fields: FieldTypes[]) {
  const isKeywordFilter = filter.operator === 'keyword';
  const isKeyValueWildcard =
    'prop' in filter &&
    filter.prop === 'tag' &&
    ['matchesWildcard', 'doesNotMatchWildcard'].includes(filter.operator);

  let fieldLabel;
  let operatorLabel;
  let valueLabel: string = '';

  if (isKeywordFilter) {
    fieldLabel = `Keyword${filter.negated ? ' is not' : ''}`;
    valueLabel = filter.value;
  } else {
    const field = getFieldObject(filter.prop, fields);

    if (field) {
      fieldLabel = field.label;
      valueLabel = getFilterValueLabel(field, filter);

      if (isKeyValueWildcard) {
        operatorLabel = `${
          filter.operator === 'matchesWildcard' ? 'contain' : 'do not contain'
        }`;
      } else {
        const operator = getOperatorObject(filter.operator, filter, fields);
        operatorLabel = operator ? operator.label : '';
      }

      /* If there's a dropdown menu for the list (eg. criticality on Entities filter),
      the option label below will be a string.
      If there's no dropdown menu (eg. List Matches in Records filter),
      there are no dropdown options but filter.value will be a string. */
      if (field.type === 'list' && 'value' in filter) {
        const listField = field as ListField;

        if (listField.options) {
          let option: ListFieldOption | NegatableListFieldOption | undefined;

          // If the options themselves have negated fields, use them in the filtering criteria.
          // Otherwise, resort to the original logic (where it's assumed that the filter values are unique)
          if (isNegatableOptionsList(listField.options)) {
            option = listField.options.find(
              (o) => o.prop === filter.value && o.negated === filter.negated
            );
          } else {
            option = listField.options.find((o) => o.prop === filter.value);
          }

          if (option) {
            valueLabel = option.label;
          }
        }
      }
    }
  }

  return {
    fieldLabel,
    operatorLabel,
    valueLabel,
  };
}

export type ProcessedField = FieldTypes & {
  parent?: ProcessedField;
  children?: ProcessedField[];
};

/**
 * Recursively create a field list based on dot notation props.
 */
export function buildFieldList(
  result: ProcessedField[],
  model: FieldTypes,
  prop: string = model.prop,
  parent?: ProcessedField
) {
  const [first, ...rest] = prop.split(model.propSeparator || '.');

  if (rest.length) {
    let has = result.find((r) => r.prop === first) as ProcessedField;

    if (!has) {
      result.push(
        (has = {
          label: model.parentLabel || humanizeToCorrectTitleCase(first),
          prop: first,
          parent,
          children: [],
        } as any)
      );
    }

    has.children = buildFieldList(
      has.children || [],
      model,
      rest.join('.'),
      has
    );
  } else {
    result.push({
      ...model,
      parent,
      label: (model as any).formattedLabel || model.label,
    });
  }

  return result;
}

/**
 * Flatten the processed fields and conditionally filter expansions.
 */
export function flattenFieldList(
  fields: ProcessedField[],
  expansions?: { [prop: string]: boolean }
) {
  const result: ProcessedField[] = [];

  for (const field of fields) {
    if (field.children) {
      result.push(field);

      if (!expansions || expansions[field.prop]) {
        result.push(...flattenFieldList(field.children, expansions));
      }
    } else {
      result.push(field);
    }
  }

  return result;
}

/**
 * Check if the numeric field requires number mask
 */
export function needNumberFormat(fieldPropName: string) {
  return !NO_NUMERIC_FORMAT_PROP_SUFFIX.some((prop) =>
    fieldPropName.endsWith(prop)
  );
}

/**
 * Applying single, quick filter for Insight, Entity, and Signal list views
 */
export function addQuickFilterToQuery(
  newFilter: ValueBasedFilterTypes,
  q?: string,
  defaultQuery?: string
) {
  const { prop } = newFilter;
  const existingFilters = deserializeQuery(q ?? defaultQuery);
  const newFilters = q?.includes(prop)
    ? existingFilters.map((filter: any) =>
        filter.prop === prop ? newFilter : filter
      )
    : [...existingFilters, newFilter];
  return serializeQuery(newFilters);
}

export const getEntityTypeFilters = () => {
  return defaultEntityTypeOptions.map(({ name, value }) => ({
    label: name,
    prop: value,
  }));
};
